import React, { useRef, useState } from 'react'
import { css } from '@emotion/core'
import { Button, LayoutBand, Separator, Image, H3, H5, Subheading, useStatusOverlay } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import { postLongPollData } from '../../shared/services/services'
import { fileToPatronPhotoDataUrl } from '../../shared/helpers/patronPhoto'
import HiddenFileInput from './HiddenFileInput'

export default function CAFGuestUpload() {
  const { query } = useQueryParams()
  const { shortcode } = query

  const mainCss = css`
    display: flex;
    align-items: center;
    flex-direction: column;
  `
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const inputRef = useRef()
  const [localImage, setLocalImage] = useState()
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // Sizing and encoding live in the shared patron-photo helper so this upload
  // and the kiosk camera capture cannot drift apart.
  const processImage = (file) => {
    fileToPatronPhotoDataUrl(file)
      .then(setLocalImage)
      .catch((error) => {
        console.error('Error: ', error)
      })
  }
  const uploadImg = () => {
    setUploading(true)
    postLongPollData({ longPollSessionId: shortcode, dataURI: localImage })
      .then((response) => {
        // postLongPollData resolves to null without posting anything when the shortcode
        // or the photo is missing, so a resolved promise is not proof the kiosk got it.
        if (!response) throw new Error('The photo was not sent to the kiosk')
        setUploadSuccess(true)
      })
      .catch((error) => {
        console.error('Error: ', error)
        // Back to the Upload button rather than a spinner that never resolves: this is a
        // walk-up kiosk visitor with no other way to retry.
        setUploading(false)
        // The service answers 400 for both ways a session can be gone - "has expired" once
        // the kiosk's window closes, "has not started" once the kiosk deleted it
        // (LongPollingService.checkValidLongPollSession). Neither survives a retry, so that
        // case gets the one instruction that helps instead of a loop that cannot succeed.
        // Everything else - a dropped connection, S3, a 500 - is worth another tap.
        const sessionIsGone = error?.response?.status === 400
        showStatusOverlay({
          message: sessionIsGone
            ? t('caf.guest-upload.invalid-link', 'This link is no longer valid. Scan the QR code at the kiosk again.')
            : t('caf.guest-upload.failed', 'Your photo could not be sent to the kiosk. Please try again.'),
          type: 'error',
        })
      })
  }

  return (
    <LayoutBand>
      <div css={mainCss}>
        {!uploadSuccess && (
          <>
            {/* Scoped to the upload state: a leading separator would swallow the ones below it
                via zion-ui's adjacent-sibling collapse once the success screen hides the H5 */}
            <Separator size="lg" />
            <H5 color="white" centered>
              {t('caf.guest-upload.select', 'Select An Image From Your Phone')}
            </H5>
          </>
        )}
        <Separator />
        {!uploadSuccess && (
          <>
            <Button
              data-testid="caf-guest-upload-select-image-button"
              emphasis="medium"
              onClick={() => inputRef.current?.click()}
              linkName="CAFGuestUpload:SELECT_IMAGE:CLICK"
            >
              {t('caf.select-image', 'Select Image')}
            </Button>
            <HiddenFileInput
              inputRef={inputRef}
              onFileAccepted={processImage}
              formTestId="caf-guest-upload-img-input-form"
              inputTestId="upload-input"
            />
          </>
        )}
        <Separator size="lg" />
        {localImage && (
          <>
            {!uploading && <Image alt="preview" maxWidth="40vw" noCrop src={localImage} />}
            <Separator size="lg" />
            {uploadSuccess ? (
              <H3 color="white" centered>
                {t('caf.guest-upload.return', 'Return to the Kiosk')}
                <Subheading>{t('caf.guest-upload.momentarily', 'Your photo will load momentarily')}</Subheading>
              </H3>
            ) : (
              <>
                {uploading ? (
                  <NoticeLoading large />
                ) : (
                  <Button emphasis="high" onClick={uploadImg} linkName="CAFGuestUpload:UPLOAD:CLICK">
                    {t('caf.guest-upload.upload', 'Upload')}
                  </Button>
                )}
              </>
            )}
            {/* Bottom spacer for the Upload button, which is otherwise the last child
                and sits flush against the bottom of the screen on a phone */}
            <Separator size="lg" />
          </>
        )}
      </div>
    </LayoutBand>
  )
}
